import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import './LoadingScreen.css';

/* SVG Server Rack — clean outline style */
function ServerRack({ side }) {
  const mirror = side === 'right';
  return (
    <svg
      viewBox="0 0 120 320"
      className={`rack-svg ${mirror ? 'rack-right' : 'rack-left'}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Rack frame */}
      <rect x="10" y="10" width="100" height="300" rx="4" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />

      {/* Rack units — 8 server slots */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const y = 22 + i * 35;
        return (
          <g key={i}>
            {/* Server unit */}
            <rect x="18" y={y} width="84" height="28" rx="2" stroke="rgba(255,255,255,0.08)" strokeWidth="1" fill="rgba(255,255,255,0.02)" />
            {/* Status LED */}
            <circle cx="28" cy={y + 14} r="2.5" fill={i < 6 ? '#DC2626' : 'rgba(255,255,255,0.06)'} opacity={i < 6 ? 0.8 : 0.3}>
              {i < 6 && (
                <animate attributeName="opacity" values="0.4;0.9;0.4" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
              )}
            </circle>
            {/* Drive bays */}
            <rect x="38" y={y + 5} width="8" height="18" rx="1" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
            <rect x="49" y={y + 5} width="8" height="18" rx="1" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
            <rect x="60" y={y + 5} width="8" height="18" rx="1" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
            {/* Ventilation lines */}
            <line x1="75" y1={y + 8} x2="94" y2={y + 8} stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
            <line x1="75" y1={y + 14} x2="94" y2={y + 14} stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
            <line x1="75" y1={y + 20} x2="94" y2={y + 20} stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
          </g>
        );
      })}

      {/* Bottom cable management */}
      <line x1="30" y1="302" x2="30" y2="316" stroke="rgba(220,38,38,0.2)" strokeWidth="1" />
      <line x1="50" y1="302" x2="50" y2="312" stroke="rgba(220,38,38,0.15)" strokeWidth="1" />
      <line x1="70" y1="302" x2="70" y2="318" stroke="rgba(220,38,38,0.2)" strokeWidth="1" />
      <line x1="90" y1="302" x2="90" y2="310" stroke="rgba(220,38,38,0.15)" strokeWidth="1" />
    </svg>
  );
}

const LoadingScreen = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [doorsOpen, setDoorsOpen] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    if (completedRef.current) return;
    const startTime = Date.now();
    const duration = 3200;

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, Math.round((elapsed / duration) * 100));
      setProgress(pct);

      if (pct >= 100) {
        clearInterval(timer);
        if (!completedRef.current) {
          completedRef.current = true;
          // Doors start opening
          setDoorsOpen(true);
          // Then fade out after doors open
          setTimeout(() => setIsComplete(true), 800);
          setTimeout(() => onLoadingComplete(), 1400);
        }
      }
    }, 30);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`loading-screen ${isComplete ? 'fade-out' : ''}`}>
      {/* Left data center door */}
      <motion.div
        className="dc-door dc-door-left"
        animate={doorsOpen ? { x: '-100%' } : { x: 0 }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      >
        <ServerRack side="left" />
      </motion.div>

      {/* Right data center door */}
      <motion.div
        className="dc-door dc-door-right"
        animate={doorsOpen ? { x: '100%' } : { x: 0 }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      >
        <ServerRack side="right" />
      </motion.div>

      {/* Center content */}
      <div className="loading-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="loading-logo"
        >
          <img
            src={process.env.PUBLIC_URL + '/assets/images/comtel-logo-darkmode.png'}
            alt="Comtel"
            className="loading-logo-img"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="loading-tagline"
        >
          Financial Infrastructure Solutions
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="loading-progress-track"
        >
          <div
            className="loading-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </motion.div>

        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: doorsOpen ? 0 : 0.4 }}
          transition={{ delay: 0.8 }}
          className="loading-status"
        >
          {progress < 30 ? 'Initializing systems...' : progress < 60 ? 'Connecting infrastructure...' : progress < 90 ? 'Securing connections...' : 'Welcome'}
        </motion.span>
      </div>
    </div>
  );
};

export default LoadingScreen;
